import { useState } from "react";
import type { ParentDigest } from "@howcomet/shared";
import { promiseAgeCopy } from "@howcomet/shared";
import { CATEGORIES } from "@howcomet/shared";
import { api } from "../api";

/**
 * The grown-ups view, doing four jobs: approve trades, set prices, see what the
 * kid was curious about, and see what's fading.
 *
 * The fading list is the honest bit and the most useful one — a sky that's
 * mostly dim says something true, and each dim star is a real dinner-table
 * question.
 */

/**
 * The digest itself, with no authentication of its own. It used to own a
 * passcode gate and live on the tablet; now it is handed a token by whichever
 * surface obtained one, which is only ever the parent portal.
 */
export function DigestView({
  token,
  digest,
  onChanged,
}: {
  token: string;
  digest: ParentDigest;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const load = (_t: string) => onChanged();

  const decide = async (id: string, decision: "approved" | "denied") => {
    setBusy(true);
    try {
      await api.decideRequest(token, id, decision);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const dismissFlag = async (id: string) => {
    setBusy(true);
    try {
      await api.parentDismissFlag(token, id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rise">

      {digest.flagged.length > 0 && (
        <Section title="Worth a conversation" tone="alert">
          <p className="mb-3 text-[15px] text-cream/70">
            These questions were not answered, and {digest.child.name} was told you would see them.
          </p>
          {digest.flagged.map((flag) => (
            <div
              key={flag.id}
              className="flex flex-wrap items-start gap-3 border-t border-cream/10 py-3 first:border-0"
            >
              <div className="min-w-[180px] flex-1">
                <p className="text-cream">“{flag.text}”</p>
                <p className="text-[13px] text-cream/45">{formatDate(flag.at)}</p>
              </div>
              {/*
                Says what it means rather than "Dismiss". The action is the
                conversation; this only puts the reminder away, and the wording
                should not invite tidying a safety flag without having had it.
              */}
              <button
                disabled={busy}
                onClick={() => void dismissFlag(flag.id)}
                className="kidbtn rounded-full bg-cream/12 px-4 py-2 text-[14px] font-extrabold text-cream"
              >
                We've talked about it
              </button>
            </div>
          ))}
        </Section>
      )}

      {/*
        Only once there is nothing outstanding. A parent with a live flag does
        not need to be told about older ones, and a permanent counter next to
        an active warning reads as a scoreboard.
      */}
      {digest.flagged.length === 0 && digest.flaggedHandled > 0 && (
        <Section title="Worth a conversation">
          <p className="text-[15px] text-cream/60">
            Nothing new. You've talked through {digest.flaggedHandled}{" "}
            {digest.flaggedHandled === 1 ? "question" : "questions"} before — those are kept, just
            not shown here.
          </p>
        </Section>
      )}

      <Section title="Waiting on you">
        {digest.pending.length === 0 ? (
          <p className="text-cream/50">No trade requests right now.</p>
        ) : (
          digest.pending.map((request) => (
            <div key={request.id} className="flex flex-wrap items-center gap-3 py-2">
              <span className="text-[24px]">{request.reward.emoji}</span>
              <span className="min-w-[140px] flex-1 font-bold text-cream">
                {request.reward.label} · ⭐ {request.reward.cost}
              </span>
              <button
                disabled={busy}
                onClick={() => void decide(request.id, "approved")}
                className="kidbtn rounded-full bg-mint px-4 py-2 font-extrabold text-night"
              >
                Approve
              </button>
              <button
                disabled={busy}
                onClick={() => void decide(request.id, "denied")}
                className="kidbtn rounded-full bg-cream/12 px-4 py-2 font-extrabold text-cream"
              >
                Not yet
              </button>
            </div>
          ))
        )}
        <p className="mt-3 text-[14px] text-cream/50">
          Approving moves the stardust into the trade. You hand over the actual reward yourself —
          the app never buys anything.
        </p>
      </Section>

      {/*
        The part that does the work.

        There is no leaderboard and no score you can lose, on purpose: ranking
        families would rank them by what they can afford, in front of their
        children. What replaces it is simply not letting a promise disappear —
        it sits here with a number of days on it, and the child can take their
        stardust back out of it.
      */}
      {digest.outstanding.length > 0 && (
        <Section title="You said yes to these" tone={digest.outstanding.some((p) => p.waitingDays >= 7) ? "alert" : undefined}>
          <p className="mb-3 text-[15px] text-cream/70">
            {digest.child.name} hasn't told us these arrived yet. Their stardust is still sitting in
            the trade — if it doesn't happen they can take it back, and the goal returns to their
            list.
          </p>
          {digest.outstanding.map((promise) => (
            <div key={promise.id} className="flex flex-wrap items-center gap-3 border-t border-cream/10 py-2 first:border-0">
              <span className="text-[24px]">{promise.emoji}</span>
              <span className="min-w-[140px] flex-1 font-bold text-cream">{promise.label}</span>
              <span className={promise.waitingDays >= 7 ? "font-bold text-pink" : "text-cream/50"}>
                {promiseAgeCopy(promise.waitingDays)}
              </span>
            </div>
          ))}
          <p className="mt-3 text-[14px] text-cream/50">
            Only {digest.child.name} can mark one of these done — a grown-up ticking off their own
            promise wouldn't tell you anything.
          </p>
        </Section>
      )}

      {digest.record.kept > 0 && (
        <Section title="Promises kept">
          <p className="text-[15px] text-cream/70">
            {digest.record.kept} kept
            {digest.record.typicalDays !== null && `, usually in about ${digest.record.typicalDays} days`}
            {digest.record.missed > 0 && `, ${digest.record.missed} taken back`}.
            {digest.record.title && (
              <>
                {" "}
                {digest.child.name} sees you as <strong className="text-amber">{digest.record.title}</strong>.
              </>
            )}
          </p>
        </Section>
      )}

      {digest.suggestions.length > 0 && (
        <Section title={`${digest.child.name}'s goal ideas`}>
          <p className="mb-3 text-[15px] text-cream/70">
            They asked for these. You set the price — that's the half they don't get to choose.
          </p>
          {digest.suggestions.map((suggestion) => (
            <SuggestionRow
              key={suggestion.id}
              token={token}
              suggestion={suggestion}
              onChanged={() => load(token)}
            />
          ))}
        </Section>
      )}

      <Section title="What they were curious about">
        {digest.curiosity.length === 0 ? (
          <p className="text-cream/50">Nothing yet this week.</p>
        ) : (
          digest.curiosity.slice(0, 10).map((entry, i) => (
            <div key={i} className="flex items-center gap-3 py-2">
              {/* Colour-coded by constellation, so a parent can see the shape of
                  a week at a glance — all science, or all over the place. */}
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: CATEGORIES[entry.category].color }}
                title={CATEGORIES[entry.category].name}
              />
              <span className="flex-1 text-cream">{entry.question}</span>
              <span className="text-[13px] text-cream/45">{formatDate(entry.at)}</span>
            </div>
          ))
        )}
      </Section>

      <Section title="Fading — worth asking about at dinner">
        {digest.fading.length === 0 ? (
          <p className="text-cream/50">Nothing is fading right now.</p>
        ) : (
          digest.fading.map((star, i) => (
            <div key={i} className="py-2">
              <p className="text-cream">{star.question}</p>
              <p className="text-[13px] text-amber">
                Last looked at {star.daysSinceLit === 0 ? "today" : `${star.daysSinceLit} days ago`}
              </p>
            </div>
          ))
        )}
      </Section>

      <Section title="Stardust rules">
        <p className="leading-relaxed text-cream/75">
          Stardust is earned by <b>finishing a check</b> (15) or <b>relighting a fading star</b>{" "}
          (10) — never by asking questions, so there is nothing to farm. Daily cap{" "}
          {digest.stardust.dailyCap}; today {digest.stardust.earnedToday}/
          {digest.stardust.dailyCap}. This week {digest.stardust.earnedThisWeek}. Balance ⭐{" "}
          {digest.stardust.balance}.
        </p>
        <p className="mt-3 font-bold text-cyan">You set the prices. The kid picks the goal.</p>
      </Section>

      <RewardEditor token={token} digest={digest} onChanged={() => load(token)} />
    </div>
  );
}

/**
 * Turning a kid's idea into a real goal. The price field is required to add it
 * — there is no "accept as-is", because a goal without a number is one the
 * child can never reach.
 */
function SuggestionRow({
  token,
  suggestion,
  onChanged,
}: {
  token: string;
  suggestion: ParentDigest["suggestions"][number];
  onChanged: () => void;
}) {
  const [cost, setCost] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (decision: "added" | "declined") => {
    setBusy(true);
    try {
      await api.decideSuggestion(token, suggestion.id, {
        decision,
        ...(decision === "added" ? { cost: Number(cost) } : {}),
      });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-cream/10 py-3 first:border-0">
      <p className="text-[18px] text-cream">“{suggestion.text}”</p>
      <p className="text-[13px] text-cream/45">{formatDate(suggestion.at)}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          value={cost}
          onChange={(e) => setCost(e.target.value.replace(/\D/g, "").slice(0, 4))}
          placeholder="Cost"
          inputMode="numeric"
          className="w-24 rounded-full bg-panel2 px-4 py-2 text-cream outline-none"
        />
        <button
          disabled={!cost || Number(cost) < 5 || busy}
          onClick={() => void decide("added")}
          className="kidbtn rounded-full bg-mint px-4 py-2 font-extrabold text-night disabled:bg-cream/12 disabled:text-cream/40"
        >
          Add at ⭐ {cost || "…"}
        </button>
        <button
          disabled={busy}
          onClick={() => void decide("declined")}
          className="kidbtn rounded-full bg-cream/12 px-4 py-2 font-bold text-cream"
        >
          Not this one
        </button>
      </div>
    </div>
  );
}

function RewardEditor({
  token,
  digest,
  onChanged,
}: {
  token: string;
  digest: ParentDigest;
  onChanged: () => void;
}) {
  const [label, setLabel] = useState("");
  const [cost, setCost] = useState("");

  return (
    <Section title="Rewards you'll honour">
      {digest.rewards.map((reward) => (
        <div key={reward.id} className="flex items-center gap-3 py-2">
          <span className="text-[22px]">{reward.emoji}</span>
          <span className="flex-1 text-cream">{reward.label}</span>
          <input
            type="number"
            defaultValue={reward.cost}
            onBlur={async (e) => {
              const next = Number(e.target.value);
              if (next > 0 && next !== reward.cost) {
                await api.upsertReward(token, { ...reward, cost: next });
                onChanged();
              }
            }}
            className="w-20 rounded-lg bg-panel2 px-2 py-1 text-right font-extrabold text-gold outline-none"
          />
          <button
            onClick={async () => {
              await api.deleteReward(token, reward.id);
              onChanged();
            }}
            aria-label={`Remove ${reward.label}`}
            className="kidbtn text-cream/45"
          >
            ✕
          </button>
        </div>
      ))}

      <div className="mt-3 flex flex-wrap gap-2">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="New reward"
          className="min-w-[140px] flex-1 rounded-full bg-panel2 px-4 py-2 text-cream outline-none"
        />
        <input
          value={cost}
          onChange={(e) => setCost(e.target.value.replace(/\D/g, ""))}
          placeholder="Cost"
          className="w-24 rounded-full bg-panel2 px-4 py-2 text-cream outline-none"
        />
        <button
          onClick={async () => {
            if (!label.trim() || !cost) return;
            await api.upsertReward(token, { label: label.trim(), cost: Number(cost) });
            setLabel("");
            setCost("");
            onChanged();
          }}
          className="kidbtn rounded-full bg-gold px-5 py-2 font-extrabold text-night"
        >
          Add
        </button>
      </div>
      <p className="mt-3 text-[14px] text-cream/50">
        Skew towards small, frequent experiences rather than one big-ticket item. Paying for
        something a child already does for free can turn curiosity into a transaction — small
        rewards you hand over in person keep it a relationship.
      </p>
    </Section>
  );
}

function Section({
  title,
  children,
  tone,
}: {
  title: string;
  children: React.ReactNode;
  tone?: "alert";
}) {
  return (
    <div
      className={`mt-5 rounded-3xl p-5 ${tone === "alert" ? "border-2 border-amber bg-panel" : "bg-panel"}`}
    >
      <p
        className={`disp mb-3 text-[18px] uppercase tracking-wide ${
          tone === "alert" ? "text-amber" : "text-gold"
        }`}
      >
        {title}
      </p>
      {children}
    </div>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  const days = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return date.toLocaleDateString(undefined, { weekday: "short" });
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
